import { MAX_HP } from "@dailywars/core/engine";
import type { GameEvent, GameState, Unit } from "@dailywars/core/engine";

import {
  ATTACK_FX_BULLET_DURATION_MS,
  ATTACK_FX_CORE_DURATION_MS,
  REPAIR_FX_DURATION_MS,
} from "#/game/animation/constants";
import { attackFxInfo } from "#/game/animation/fromEventsAttackFx";
import type { CanvasBoardAnimation, CanvasFloatingNumber } from "#/game/animation/types";

function repairFxNumbers(events: GameEvent[], afterState: GameState): CanvasFloatingNumber[] {
  const numbers: CanvasFloatingNumber[] = [];
  for (const event of events) {
    if (event.type !== "unitRepaired" || event.hpRestored <= 0) continue;
    const unit = afterState.units.find((u) => u.id === event.unitId);
    if (!unit) continue;
    const percent = Math.max(1, Math.round((event.hpRestored / MAX_HP) * 100));
    numbers.push({ tile: event.pos, percent, kind: "repair" });
  }
  return numbers;
}

function collectCombatHpDisplayUnits({
  beforeState,
  events,
  afterState,
}: {
  beforeState: GameState;
  events: GameEvent[];
  afterState: GameState;
}): Unit[] {
  const attacked = events.find(
    (e): e is Extract<GameEvent, { type: "attacked" }> => e.type === "attacked",
  );
  if (!attacked) return [];

  const unitIds = new Set([attacked.attackerId, attacked.targetId]);
  const countered = events.find(
    (e): e is Extract<GameEvent, { type: "countered" }> => e.type === "countered",
  );
  if (countered) unitIds.add(countered.targetId);

  const overrides: Unit[] = [];
  for (const unitId of unitIds) {
    const before = beforeState.units.find((unit) => unit.id === unitId);
    const after = afterState.units.find((unit) => unit.id === unitId);
    if (!before || !after || before.hp === after.hp) continue;
    overrides.push({ ...before });
  }
  return overrides;
}

function collectCombatGhostUnits({
  beforeState,
  events,
  afterState,
}: {
  beforeState: GameState;
  events: GameEvent[];
  afterState: GameState;
}): Unit[] {
  const ghostById = new Map<string, Unit>();
  const attacked = events.find(
    (e): e is Extract<GameEvent, { type: "attacked" }> => e.type === "attacked",
  );
  if (!attacked) return [];

  for (const unitId of [attacked.attackerId, attacked.targetId]) {
    const inAfterState = afterState.units.some((unit) => unit.id === unitId);
    if (inAfterState) continue;
    const inBeforeState = beforeState.units.find((unit) => unit.id === unitId);
    if (inBeforeState) {
      ghostById.set(unitId, { ...inBeforeState });
    }
  }

  return [...ghostById.values()];
}

function stampAnimationQueue(anims: CanvasBoardAnimation[], nowMs: number): CanvasBoardAnimation[] {
  if (anims.length === 0) return anims;
  const [first, ...rest] = anims;
  return [{ ...first, startedAtMs: nowMs }, ...rest.map((a) => ({ ...a, startedAtMs: 0 }))];
}

function applyMovedGhostPositions({
  ghostUnits,
  moved,
  beforeState,
  afterState,
}: {
  ghostUnits: Unit[];
  moved: Extract<GameEvent, { type: "moved" }>;
  beforeState: GameState;
  afterState: GameState;
}): Unit[] {
  const movedTo = moved.path[moved.path.length - 1]!;
  const movedGhost = ghostUnits.find((ghost) => ghost.id === moved.unitId);
  if (movedGhost) {
    movedGhost.pos = movedTo;
  }
  if (!afterState.units.some((unit) => unit.id === moved.unitId)) {
    const movedUnitBefore = beforeState.units.find((unit) => unit.id === moved.unitId);
    if (movedUnitBefore && !ghostUnits.some((ghost) => ghost.id === movedUnitBefore.id)) {
      return [...ghostUnits, { ...movedUnitBefore }];
    }
  }
  return ghostUnits;
}

function appendCombatAnimations({
  result,
  moved,
  ghostUnits,
  hpDisplayUnits,
  fxInfo,
  nowMs,
}: {
  result: CanvasBoardAnimation[];
  moved: Extract<GameEvent, { type: "moved" }> | undefined;
  ghostUnits: Unit[];
  hpDisplayUnits: Unit[];
  fxInfo: ReturnType<typeof attackFxInfo>;
  nowMs: number;
}): void {
  const hpDisplay = hpDisplayUnits.length > 0 ? { hpDisplayUnits } : {};
  if (moved && moved.path.length > 0) {
    result.push({
      type: "move",
      unitId: moved.unitId,
      from: moved.from,
      path: moved.path,
      startedAtMs: nowMs,
      durationMs: Math.max(180, moved.path.length * 130),
      ...(ghostUnits.length > 0 ? { ghostUnits } : {}),
      ...hpDisplay,
    });
  }

  if (fxInfo) {
    result.push({
      type: "attackFx",
      attackerTile: fxInfo.attackerTile,
      targetTile: fxInfo.targetTile,
      startedAtMs: nowMs,
      durationMs: (fxInfo.direct ? 0 : ATTACK_FX_BULLET_DURATION_MS) + ATTACK_FX_CORE_DURATION_MS,
      damageNumbers: fxInfo.damageNumbers,
      tracers: fxInfo.tracers,
      hasCounter: fxInfo.hasCounter,
      direct: fxInfo.direct,
      ...(ghostUnits.length > 0 ? { ghostUnits } : {}),
      ...hpDisplay,
    });
  }
}

export function interpretEvents({
  beforeState,
  events,
  afterState,
}: {
  beforeState: GameState;
  events: GameEvent[];
  afterState: GameState;
}): CanvasBoardAnimation[] {
  const nowMs = typeof performance !== "undefined" ? performance.now() : 0;
  const fxInfo = attackFxInfo({ beforeState, events, afterState });
  const hpDisplayUnits = collectCombatHpDisplayUnits({ beforeState, events, afterState });
  let ghostUnits = collectCombatGhostUnits({ beforeState, events, afterState });
  const moved = events.find(
    (event): event is Extract<GameEvent, { type: "moved" }> => event.type === "moved",
  );

  if (moved && moved.path.length > 0) {
    ghostUnits = applyMovedGhostPositions({ ghostUnits, moved, beforeState, afterState });
  }

  const result: CanvasBoardAnimation[] = [];
  appendCombatAnimations({ result, moved, ghostUnits, hpDisplayUnits, fxInfo, nowMs });

  if (result.length > 0) {
    return stampAnimationQueue(result, nowMs);
  }

  const repairs = repairFxNumbers(events, afterState);
  if (repairs.length > 0) {
    return [
      {
        type: "repairFx",
        numbers: repairs,
        startedAtMs: nowMs,
        durationMs: REPAIR_FX_DURATION_MS,
      },
    ];
  }

  return [];
}
